import { useState, useMemo } from 'react';
import { Check, Link2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useOrgPeople, OrgPerson } from '@/hooks/useOrgPeople';

interface AssigneeSelectProps {
  value: string | undefined;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
}

export function AssigneeSelect({
  value,
  onChange,
  placeholder = 'Select assignee...',
  className,
}: AssigneeSelectProps) {
  const [open, setOpen] = useState(false);
  const { activeOrgPeople, isLoading } = useOrgPeople();

  // Sort: ClickUp-mapped users first, then alphabetically
  const sortedPeople = useMemo(() => {
    return [...activeOrgPeople].sort((a, b) => {
      const aHasClickUp = !!a.clickup_user_id;
      const bHasClickUp = !!b.clickup_user_id;
      if (aHasClickUp && !bHasClickUp) return -1;
      if (!aHasClickUp && bHasClickUp) return 1;
      return a.name.localeCompare(b.name);
    });
  }, [activeOrgPeople]);

  // Find selected person
  const selectedPerson = useMemo(() => {
    if (!value) return null;
    return activeOrgPeople.find(
      (p) => p.name.toLowerCase() === value.toLowerCase()
    );
  }, [value, activeOrgPeople]);

  const handleSelect = (person: OrgPerson) => {
    onChange(person.name);
    setOpen(false);
  };

  const handleClear = () => {
    onChange('');
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          className={cn(
            'text-xs font-medium transition-colors cursor-pointer',
            'hover:opacity-70',
            !value && 'text-muted-foreground italic',
            value && 'text-foreground',
            className
          )}
        >
          <span className="flex items-center gap-1 truncate">
            {selectedPerson ? (
              <>
                <span className="truncate">{selectedPerson.name}</span>
                {selectedPerson.clickup_user_id && (
                  <Link2 className="h-2.5 w-2.5 shrink-0 text-success" />
                )}
              </>
            ) : value ? (
              <span className="truncate">{value}</span>
            ) : (
              placeholder
            )}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[220px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search team..." className="h-8 text-xs" />
          <CommandList>
            <CommandEmpty>
              {isLoading ? 'Loading...' : 'No team members found.'}
            </CommandEmpty>
            {value && (
              <CommandGroup>
                <CommandItem
                  onSelect={handleClear}
                  className="text-xs text-muted-foreground"
                >
                  <span className="italic">Clear assignee</span>
                </CommandItem>
              </CommandGroup>
            )}
            <CommandGroup heading="Team Members">
              {sortedPeople.map((person) => (
                <CommandItem
                  key={person.id}
                  value={person.name}
                  onSelect={() => handleSelect(person)}
                  className="text-xs"
                >
                  <Check
                    className={cn(
                      'mr-2 h-3 w-3',
                      selectedPerson?.id === person.id
                        ? 'opacity-100'
                        : 'opacity-0'
                    )}
                  />
                  <span className="flex-1 truncate">{person.name}</span>
                  {person.clickup_user_id && (
                    <Link2 className="ml-1 h-3 w-3 text-success" title="Linked to ClickUp" />
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
